import type { OpeningSide } from "building-types/openings/openingTypes";
import { DIST_EPSILON } from "geom/core";
import {
  polyLine2AABB2,
  polyLine2Copy,
  polyLine2CopySimplify,
  polyLine2EdgeCount,
  polyLine2IsClosed,
} from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import type { PolyLineEdgeType } from "geom/polyLineTypes";
import { vec2Dist } from "geom/vec2";

export interface OpeningProfileSection {
  readonly polyLine: PolyLine2;
  readonly edgeType: PolyLineEdgeType;
}

export interface OpeningProfileWithEdgeTypes {
  readonly outline: PolyLine2;
  readonly outlineEdgeTypes: readonly PolyLineEdgeType[];
}

/**
 * Flattens ordered semantic profile sections while preserving an edge type for every generated edge.
 */
export function openingProfileFromSections(sections: readonly OpeningProfileSection[]): OpeningProfileWithEdgeTypes {
  const coordinates: number[] = [];
  const outlineEdgeTypes: PolyLineEdgeType[] = [];

  for (const section of sections) {
    const polyLine =
      polyLine2EdgeCount(section.polyLine) === 1
        ? polyLine2Copy(section.polyLine)
        : polyLine2CopySimplify(section.polyLine);
    if (polyLine.length < 4) {
      continue;
    }

    if (coordinates.length === 0) {
      coordinates.push(...polyLine);
    } else {
      const previousX = coordinates[coordinates.length - 2];
      const previousY = coordinates[coordinates.length - 1];
      if (!(vec2Dist(previousX, previousY, polyLine[0], polyLine[1]) <= DIST_EPSILON)) {
        console.assert(false, "Opening profile sections must connect");
      }
      coordinates.push(...polyLine.slice(2));
    }

    for (let edgeIndex = 0; edgeIndex < polyLine2EdgeCount(polyLine); edgeIndex++) {
      outlineEdgeTypes.push(section.edgeType);
    }
  }

  const outline = polyLine2New(coordinates);
  if (!(polyLine2IsClosed(outline))) {
    console.assert(false, "Opening profile sections must form a closed outline");
  }
  if (!(outlineEdgeTypes.length === polyLine2EdgeCount(outline))) {
    console.assert(false, "Opening profile edge types must correspond to outline edges");
  }

  return { outline, outlineEdgeTypes };
}

export function makeOpeningSide(
  from: Partial<OpeningSide> & Pick<OpeningSide, "opening2" | "openingRect2">,
): OpeningSide {
  const {
    opening2,
    openingRect2,
    openingCasing2,
    headCasing = [],
    trim = [],
    sideCasing = [],
    hasCasing = true,
    context = {},
  } = from;

  const { minX, maxX } = polyLine2AABB2(opening2);

  const { startParam = minX, endParam = maxX } = from;

  return {
    ...from,
    trim,
    hasCasing,
    headCasing,
    sideCasing,
    opening2,
    openingCasing2: openingCasing2 || polyLine2Copy(opening2),
    openingRect2,
    openingOffsets2: {},
    openingOffsets3: {},
    openingSideLayers: {},
    startParam,
    endParam,
    centerParam: (startParam + endParam) / 2,
    context,
  };
}
